import React, { useState } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Lock, Unlock, Check, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface ManualLossStreakEditorProps {
  botId: string;
  currentStreak: number;
}

export const ManualLossStreakEditor: React.FC<ManualLossStreakEditorProps> = ({
  botId,
  currentStreak,
}) => {
  const queryClient = useQueryClient();
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [newStreak, setNewStreak] = useState(currentStreak);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Fetch conditional skip settings for this bot
  const { data: skipSettings } = useQuery({
    queryKey: ['conditional-skip-settings', botId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('conditional_skip_settings')
        .select('*')
        .eq('bot_id', botId)
        .single();
      
      if (error) throw error;
      return data;
    },
  });

  // Enhanced mutation to update loss streak and recalculate lock/mode status
  const updateStreakMutation = useMutation({
    mutationFn: async (streak: number) => {
      if (!skipSettings) {
        throw new Error('Skip settings not loaded');
      }

      // Calculate new lock status based on business logic
      const shouldLock = streak >= skipSettings.max_loss_streak && 
                        skipSettings.max_loss_behavior === 'lock_gate';
      
      // Calculate new mode based on threshold
      const shouldOpen = streak >= skipSettings.loss_streak_threshold;
      const newMode = shouldOpen ? 'OPEN' : 'GATE';
      const openedAt = shouldOpen ? new Date().toISOString() : null;

      const updateData = {
        current_loss_streak: streak,
        locked_by_max_loss: shouldLock,
        mode: newMode,
        opened_at: openedAt,
        updated_at: new Date().toISOString(),
      };

      const { data, error } = await supabase
        .from('bot_current_condition_skip')
        .update(updateData)
        .eq('bot_id', botId)
        .select()
        .single();
      
      if (error) throw error;
      return { data, changes: updateData };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['conditional-skip-states'] });
      queryClient.invalidateQueries({ queryKey: ['conditional-skip-states-monitor'] });
      
      const { changes } = result;
      let statusMessage = `Loss streak updated to ${changes.current_loss_streak}`;
      
      if (changes.locked_by_max_loss) {
        statusMessage += ' - Bot LOCKED due to max loss streak';
      } else if (changes.mode === 'OPEN') {
        statusMessage += ' - Bot OPENED (threshold reached)';
      } else {
        statusMessage += ' - Bot in GATE mode';
      }
      
      toast.success(statusMessage);
      handleCancel();
    },
    onError: (error: any) => {
      toast.error(`Failed to update loss streak: ${error.message}`);
    },
  });

  const handleUnlock = () => {
    setIsUnlocked(true);
    setNewStreak(currentStreak);
  };

  const handleCancel = () => {
    setIsUnlocked(false);
    setNewStreak(currentStreak);
    setShowConfirmDialog(false);
  };

  const handleSave = () => {
    if (newStreak === currentStreak) {
      handleCancel();
      return;
    }

    if (newStreak < 0) {
      toast.error('Loss streak cannot be negative');
      return;
    }

    setShowConfirmDialog(true);
  };

  const handleConfirmSave = () => {
    updateStreakMutation.mutate(newStreak);
    setShowConfirmDialog(false);
  };

  if (!isUnlocked) {
    return (
      <div className="flex items-center gap-2">
        <span className="font-mono text-lg">
          {currentStreak}
        </span>
        <Button
          size="sm"
          variant="outline"
          onClick={handleUnlock}
          className="h-8 w-8 p-0"
          title="Unlock to edit loss streak"
        >
          <Lock className="h-4 w-4 text-muted-foreground" />
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center gap-2">
        <Input
          type="number"
          min="0"
          value={newStreak}
          onChange={(e) => setNewStreak(parseInt(e.target.value) || 0)}
          className="w-20 font-mono"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSave();
            } else if (e.key === 'Escape') {
              handleCancel();
            }
          }}
        />
        <Button
          size="sm"
          variant="outline"
          onClick={handleSave}
          disabled={updateStreakMutation.isPending}
          className="h-8 w-8 p-0"
          title="Save changes"
        >
          {updateStreakMutation.isPending ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Check className="h-3 w-3 text-green-600" />
          )}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={handleCancel}
          disabled={updateStreakMutation.isPending}
          className="h-8 w-8 p-0"
          title="Cancel changes"
        >
          <X className="h-3 w-3 text-red-600" />
        </Button>
        <div className="flex items-center">
          <Unlock className="h-4 w-4 text-green-600" />
        </div>
      </div>

      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Loss Streak Change</DialogTitle>
            <DialogDescription>
              Are you sure you want to change the loss streak from{' '}
              <span className="font-mono font-semibold">{currentStreak}</span> to{' '}
              <span className="font-mono font-semibold">{newStreak}</span>?
              <br />
              <br />
              This will affect the bot's conditional skip behavior and cannot be undone automatically.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowConfirmDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmSave}
              disabled={updateStreakMutation.isPending}
            >
              {updateStreakMutation.isPending && (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              )}
              Confirm Change
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};